/**
 * EnrollmentPromptBanner Component
 *
 * Dismissible banner prompting users to complete biometric enrollment.
 * Displayed on dashboard when user is not enrolled.
 *
 * FERP-PII-052: Enrollment prompt for L1/L1G protection.
 */

import React from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Alert,
  Button,
  Group,
  Text,
  Stack,
} from '@mantine/core';
import {
  IconFingerprint,
  IconShieldCheck,
  IconX,
} from '@tabler/icons-react';

import { EnrollmentPrompt, SkipReason } from '../hooks/useEnrollment';

export interface EnrollmentPromptBannerProps {
  /** Prompt data from backend */
  prompt: EnrollmentPrompt;

  /** Called when user dismisses the banner */
  onDismiss?: () => void;

  /** Called when user skips enrollment */
  onSkip?: (reason: SkipReason) => void;
}

export const EnrollmentPromptBanner: React.FC<EnrollmentPromptBannerProps> = ({
  prompt,
  onDismiss,
  onSkip,
}) => {
  const navigate = useNavigate();

  const handleSetup = () => {
    navigate('/enrollment');
  };

  const handleSkip = () => {
    onSkip?.(SkipReason.REMIND_LATER);
    onDismiss?.();
  };

  return (
    <Alert
      color="blue"
      icon={<IconShieldCheck size={24} />}
      title={prompt.title || 'Protect Your Sensitive Information'}
      withCloseButton={prompt.canSkip}
      onClose={onDismiss}
      mb="lg"
    >
      <Stack gap="sm">
        <Text size="sm">
          {prompt.message || 'Set up biometric protection to secure your SSN, bank accounts, and other sensitive data. Only you will be able to access this information.'}
        </Text>

        <Group gap="sm">
          <Button
            leftSection={<IconFingerprint size={16} />}
            onClick={handleSetup}
            size="sm"
          >
            Set Up Now
          </Button>
          {prompt.canSkip && (
            <Button
              variant="subtle"
              onClick={handleSkip}
              size="sm"
            >
              Remind Me Later
            </Button>
          )}
        </Group>
      </Stack>
    </Alert>
  );
};
